/**
 *  Define how we want to poll a rest api
 *
 *  all number is milliseconds, except for maxPendingPollRequests
 */
export interface PollingModel {
    defaultInterval: number,
    // the max number of overlapping poll requests allowed
    maxPendingPollRequests: number
}

const defaultPollingModel: PollingModel = {
    defaultInterval: 3000,
    maxPendingPollRequests: 1
};

export class Poller<T> {
    private model: PollingModel;
    private pending: number[];
    private currentInterval: number;
    private stopped: boolean;
    private subscribers: Function[] = [];

    constructor(model: PollingModel, private poll: () => JQueryPromise<T>,  private onResult: (data: T) => void) {
        this.model = _.extend({}, defaultPollingModel, model || undefined);
        this.pending = [];
        this.currentInterval = this.model.defaultInterval;
        this.stopped = true;
    }

    /**
     * TODO: adjust the current interval based on previous result
     */
    private getCurrentInterval(): number {
        return this.currentInterval;
    }

    private invokePoll(timeout: number) {
        this.poll().done((data: T) => {
            this.onResult(data);
            // clear the timeout
            this.pending = _.filter(this.pending, x => x !== timeout);
            // schedule for the next poll
            if (!this.stopped) {
                this.start();
            }
        });
    }

    public start() {
        this.stopped = false;
        if (this.pending.length < this.model.maxPendingPollRequests) {
            let cur = this;
            // NOTE: the following call to setTimeout must use 
            // javascript function and var syntax to get the timeout
            // variable captured correctly, DO NOT change it to 'let' and lambda
            // expression.
            var timeout = setTimeout(function() {
                cur.invokePoll(timeout);
            }, this.getCurrentInterval()); 
            this.pending.push(timeout);
        }
    }

    public stop() {
        this.stopped = true;
        _.each(this.pending, clearTimeout);
        this.pending = [];
        this.subscribers.forEach(fn => fn());
    }

    public isStopped(): boolean {
        return this.stopped;
    }

    public onStop(handler: Function) {
        this.subscribers.push(handler);
    }
}


/**
 * make function that return promise retriable on a schedule
 * 
 * TODO: rewrite as decorator
 * @param inner inner function that try to resolve a promise
 * @param interval wait interval between retry
 * @param maxDuration maximum duration before rejecting
 */
export function pollerPromise<T>(inner: () => JQueryPromise<T>, interval: number, maxDuration: number): JQueryPromise<T> {
    const deferred= $.Deferred<T>();
    const start = new Date().getTime();
    const action = function() {
        inner().then(x => deferred.resolve(x), (e) => {
            const now = new Date().getTime();
            if (maxDuration > 0 && now - start >= maxDuration) {
                deferred.reject(e);
            } else {
                setTimeout(action, interval);
            }
        });
    };
    action();
    return deferred;
}


// WEBPACK FOOTER //
// ./Client/Models/PollingModel.ts